import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '../api/adminClient';
import StatusBadge from './StatusBadge';
import CompSubModal from './CompSubModal';

type Member = {
  _id: string;
  email: string;
  name?: string;
  status: 'ACTIVE' | 'PAUSED' | 'DISCONNECTED';
  role: 'USER' | 'OPERATOR';
  subscription_plan?: 'STARTER' | 'PRO' | 'PREMIUM' | null;
  subscription_started_at?: string | null;
  subscription_expires_at?: string | null;
  auto_send?: boolean;
  paused_reason?: string | null;
  daily_limit?: number;
  preferences?: any;
  voice_profile?: any;
  comp_history?: Array<{ plan: string; granted_at: string; expires_at: string; note?: string }>;
};

type DrawerData = { member: Member; recent_applications: any[] };

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function dateInputToISO(value: string): string | null {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function MemberDetailDrawer({
  memberId,
  onClose,
}: {
  memberId: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<DrawerData | { error: true } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [comp, setComp] = useState(false);
  const [startedAt, setStartedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const load = useCallback(() => {
    adminApi
      .getMember(memberId)
      .then((d) => {
        setData(d as DrawerData);
        setErr(null);
        const m = (d as DrawerData).member;
        setStartedAt(toDateInputValue(m.subscription_started_at));
        setExpiresAt(toDateInputValue(m.subscription_expires_at));
      })
      .catch(() => setData({ error: true }));
  }, [memberId]);

  useEffect(() => {
    load();
  }, [load]);

  const member = data && 'member' in data ? data.member : null;

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setErr(null);
    try {
      await fn();
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const togglePause = () => {
    if (!member) return;
    const next = member.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    return run('pause', () =>
      adminApi.patchMember(member._id, {
        status: next,
        paused_reason: next === 'PAUSED' ? 'manual_admin' : undefined,
      }),
    );
  };

  const toggleAutoSend = () => {
    if (!member) return;
    return run('autosend', () =>
      adminApi.patchMember(member._id, { auto_send: !member.auto_send }),
    );
  };

  const flipRole = () => {
    if (!member) return;
    const next = member.role === 'OPERATOR' ? 'USER' : 'OPERATOR';
    return run('role', () => adminApi.setRole(member._id, next));
  };

  const savePeriod = () => {
    if (!member) return;
    return run('period', () =>
      adminApi.setPeriod(member._id, {
        started_at: startedAt ? dateInputToISO(startedAt) : null,
        expires_at: expiresAt ? dateInputToISO(expiresAt) : null,
      }),
    );
  };

  const periodDirty =
    !!member &&
    (toDateInputValue(member.subscription_started_at) !== startedAt ||
      toDateInputValue(member.subscription_expires_at) !== expiresAt);

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-[520px] bg-base-100 p-6 overflow-y-auto shadow-xl">
        <button className="btn btn-sm btn-ghost mb-2" onClick={onClose}>
          ← Cerrar
        </button>
        {!data && <div>Cargando…</div>}
        {data && 'error' in data && <div className="alert alert-error">No se pudo cargar.</div>}
        {member && (
          <>
            <h2 className="text-xl font-bold">{member.email}</h2>
            <div className="flex gap-2 my-2 flex-wrap items-center">
              <StatusBadge value={member.status} />
              <StatusBadge value={member.role} />
              {member.subscription_plan && (
                <span className="badge badge-outline">{member.subscription_plan}</span>
              )}
              {member.auto_send ? (
                <span className="badge badge-success">auto_send: on</span>
              ) : (
                <span className="badge badge-ghost">auto_send: off</span>
              )}
              {member.paused_reason && (
                <span className="text-xs opacity-60">({member.paused_reason})</span>
              )}
            </div>

            {/* ─── Admin actions ───────────────────────────────────────── */}
            <section className="border border-base-300 rounded-box p-4 mt-4 space-y-3">
              <h3 className="font-semibold">Acciones de admin</h3>
              {err && <div className="alert alert-error text-xs">{err}</div>}

              <div className="flex flex-wrap gap-2">
                <button
                  className="btn btn-sm"
                  onClick={togglePause}
                  disabled={busy !== null}
                >
                  {busy === 'pause'
                    ? '…'
                    : member.status === 'ACTIVE'
                      ? 'Pausar usuario'
                      : 'Reanudar usuario'}
                </button>
                <button
                  className="btn btn-sm"
                  onClick={toggleAutoSend}
                  disabled={busy !== null}
                >
                  {busy === 'autosend'
                    ? '…'
                    : member.auto_send
                      ? 'Detener envíos'
                      : 'Activar envíos'}
                </button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => setComp(true)}
                  disabled={busy !== null}
                >
                  Conceder suscripción
                </button>
                <button
                  className={`btn btn-sm ${member.role === 'OPERATOR' ? 'btn-warning' : ''}`}
                  onClick={flipRole}
                  disabled={busy !== null}
                >
                  {busy === 'role'
                    ? '…'
                    : member.role === 'OPERATOR'
                      ? 'Quitar operador'
                      : 'Promover a operador'}
                </button>
              </div>

              <div className="pt-3 border-t border-base-300">
                <div className="text-sm font-medium mb-2">Override período de suscripción</div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-xs">
                    Inicio
                    <input
                      type="date"
                      className="input input-bordered input-sm w-full"
                      value={startedAt}
                      onChange={(e) => setStartedAt(e.target.value)}
                    />
                  </label>
                  <label className="text-xs">
                    Vencimiento
                    <input
                      type="date"
                      className="input input-bordered input-sm w-full"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                    />
                  </label>
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={savePeriod}
                    disabled={busy !== null || !periodDirty}
                  >
                    {busy === 'period' ? '…' : 'Aplicar período'}
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => {
                      setStartedAt(toDateInputValue(member.subscription_started_at));
                      setExpiresAt(toDateInputValue(member.subscription_expires_at));
                    }}
                    disabled={busy !== null || !periodDirty}
                  >
                    Descartar
                  </button>
                </div>
                <div className="text-xs opacity-60 mt-1">
                  Deja una fecha vacía para limpiarla. Vencimiento controla cuándo el usuario
                  pierde el plan.
                </div>
              </div>
            </section>

            {/* ─── Read-only sections ──────────────────────────────────── */}
            <h3 className="font-semibold mt-4">Preferencias</h3>
            <pre className="text-xs bg-base-200 p-2 rounded overflow-x-auto">
              {JSON.stringify(member.preferences, null, 2)}
            </pre>
            {member.voice_profile && (
              <>
                <h3 className="font-semibold mt-4">Voice profile</h3>
                <pre className="text-xs bg-base-200 p-2 rounded overflow-x-auto">
                  {JSON.stringify(member.voice_profile, null, 2)}
                </pre>
              </>
            )}
            {member.comp_history && member.comp_history.length > 0 && (
              <>
                <h3 className="font-semibold mt-4">Comp history</h3>
                <ul className="text-xs space-y-1">
                  {member.comp_history.map((c, i) => (
                    <li key={i}>
                      {c.plan} · concedido {new Date(c.granted_at).toLocaleDateString()} · expira{' '}
                      {new Date(c.expires_at).toLocaleDateString()}
                      {c.note ? ` · ${c.note}` : ''}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <h3 className="font-semibold mt-4">Postulaciones recientes</h3>
            <ul className="text-xs space-y-1">
              {(data as DrawerData).recent_applications.map((a: any) => (
                <li key={a._id}>
                  <StatusBadge value={a.status} /> ·{' '}
                  {new Date(a.created_at).toLocaleDateString()} · {a.email_subject}
                </li>
              ))}
            </ul>
          </>
        )}
        {comp && member && (
          <CompSubModal
            memberId={member._id}
            onClose={() => setComp(false)}
            onDone={() => {
              setComp(false);
              load();
            }}
          />
        )}
      </div>
    </div>
  );
}
